import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  ArrowLeftIcon,
  CpuChipIcon,
  ChartBarIcon,
  CheckCircleIcon,
  XCircleIcon,
  MapPinIcon
} from '@heroicons/react/24/outline';
import { apiService, getApiUrl, getStoredAuthToken } from '../services/api.ts';
import { LanguageSwitcher } from '../components/LanguageSwitcher.tsx';

interface DeviceDetailModel {
  device_id: string;
  name: string;
  status: string;
  is_online: boolean;
  model: string;
  location_name?: string;
  location_address?: string;
  uptime_seconds: number;
  current_state: string;
  last_heartbeat: string;
  last_seen: string;
  metrics?: {
    cpu_usage: number;
    memory_usage: number;
    gpu_usage: number;
    gpu_memory_usage: number;
    cpu_temperature: number;
    gpu_temperature: number;
    inference_fps: number;
    camera_fps: number;
  };
}

interface DeviceMetric {
  timestamp: string;
  cpu_usage: number;
  memory_usage: number;
  gpu_usage: number;
  gpu_memory_usage: number;
  cpu_temperature: number;
  gpu_temperature: number;
  inference_fps: number;
  camera_fps: number;
}

function DeviceDetails() {
  const { t, i18n } = useTranslation();
  const { deviceId } = useParams<{ deviceId: string }>();
  const navigate = useNavigate();
  const [device, setDevice] = useState<DeviceDetailModel | null>(null);
  const [metrics, setMetrics] = useState<DeviceMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeRange, setTimeRange] = useState<'1h' | '24h' | '7d'>('24h');
  const [isEditingLocation, setIsEditingLocation] = useState(false);
  const [editedLocationName, setEditedLocationName] = useState('');
  const [editedLocationAddress, setEditedLocationAddress] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (deviceId) {
      fetchDeviceDetails();
      fetchDeviceMetrics();
    }
  }, [deviceId, timeRange]);

  const fetchDeviceDetails = async () => {
    try {
      setLoading(true);
      const deviceData = await apiService.getDeviceDetails(deviceId!);
      setDevice(deviceData);
      setEditedLocationName(deviceData.location_name || '');
      setEditedLocationAddress(deviceData.location_address || '');
    } catch (err) {
      setError(i18n.t('deviceDetails.fetchError'));
      console.error('Error fetching device details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveLocation = async () => {
    try {
      setIsSaving(true);
      const token = getStoredAuthToken();
      const response = await fetch(getApiUrl(`/api/devices/${deviceId}/location`), {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          location_name: editedLocationName,
          location_address: editedLocationAddress,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update location');
      }

      // Refresh device details
      await fetchDeviceDetails();
      setIsEditingLocation(false);
    } catch (err) {
      console.error('Error updating location:', err);
      alert(t('deviceDetails.updateLocationError'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedLocationName(device?.location_name || '');
    setEditedLocationAddress(device?.location_address || '');
    setIsEditingLocation(false);
  };

  const fetchDeviceMetrics = async () => {
    try {
      const hours = timeRange === '1h' ? 1 : timeRange === '24h' ? 24 : 168;
      const metricsData = await apiService.getDeviceMetrics(deviceId!, hours);
      setMetrics(metricsData.metrics || []);
    } catch (err) {
      console.error('Error fetching device metrics:', err);
    }
  };

  const getConnectionStatusColor = (isOnline: boolean) => {
    return isOnline ? 'text-regina-success' : 'text-regina-error';
  };

  const getConnectionStatusIcon = (isOnline: boolean) => {
    return isOnline 
      ? <CheckCircleIcon className="w-5 h-5 text-regina-success" />
      : <XCircleIcon className="w-5 h-5 text-regina-error" />;
  };

  const formatUptime = (seconds: number) => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const loc = i18n.language?.startsWith('en') ? 'en-US' : 'ro-RO';
    return new Date(timestamp).toLocaleString(loc);
  };

  const formatState = (state: string) => {
    if (!state) return t('common.na');
    // Convert snake_case or SCREAMING_SNAKE_CASE to Title Case
    return state
      .toLowerCase()
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };


  if (loading) {
    return (
      <div className="relative min-h-screen bg-regina-gray-50 flex items-center justify-center">
        <div className="fixed top-3 right-3 z-50">
          <LanguageSwitcher />
        </div>
        <div className="text-regina-gray-800 text-xl">{t('deviceDetails.loading')}</div>
      </div>
    );
  }

  if (error || !device) {
    return (
      <div className="relative min-h-screen bg-regina-gray-50 flex items-center justify-center">
        <div className="fixed top-3 right-3 z-50">
          <LanguageSwitcher />
        </div>
        <div className="text-center">
          <div className="text-regina-error text-xl mb-4">{t('deviceDetails.loadErrorTitle')}</div>
          <button
            onClick={() => navigate('/reports')}
            className="px-4 py-2 bg-regina-red text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            {t('deviceDetails.backToDevices')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative space-y-4 sm:space-y-6">
      <div className="fixed top-3 right-3 z-50">
        <LanguageSwitcher />
      </div>
      {/* Header */}
      <div>
        <button
          type="button"
          onClick={() => navigate('/reports')}
          className="mb-4 flex items-center font-medium text-regina-gray-600 transition-colors hover:text-regina-red"
        >
          <ArrowLeftIcon className="mr-2 h-5 w-5" />
          {t('deviceDetails.backToDevices')}
        </button>
        
        {/* Device Info Card */}
        <div className="rounded-xl border border-regina-gray-100 bg-white p-4 shadow-sm sm:p-6">
          <div className="mb-4 flex flex-col gap-4 border-b border-regina-gray-100 pb-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h1 className="text-xl font-bold text-regina-gray-800 sm:text-2xl">{device.name}</h1>
              <p className="mt-1 break-all text-sm text-regina-gray-600">
                {t('deviceDetails.deviceIdLabel')} {device.device_id}
              </p>
            </div>
            
            <div className="flex shrink-0 items-center space-x-3 self-start sm:self-auto">
              {getConnectionStatusIcon(device.is_online)}
              <span className={`text-base font-semibold sm:text-lg ${getConnectionStatusColor(device.is_online)}`}>
                {device.is_online ? 'ONLINE' : 'OFFLINE'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Device Information Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Basic Information */}
        <div className="bg-white rounded-xl p-6 border border-regina-gray-100 shadow-sm">
          <h3 className="text-lg font-semibold text-regina-gray-800 mb-4">{t('deviceDetails.infoTitle')}</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b border-regina-gray-100">
              <span className="text-sm text-regina-gray-600">{t('deviceDetails.model')}</span>
              <span className="text-sm font-semibold text-regina-gray-800">{device.model}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-regina-gray-100">
              <span className="text-sm text-regina-gray-600">{t('deviceDetails.uptime')}</span>
              <span className="text-sm font-semibold text-regina-gray-800">{formatUptime(device.uptime_seconds)}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-regina-gray-100">
              <span className="text-sm text-regina-gray-600">{t('deviceDetails.currentState')}</span>
              <span className="text-sm font-semibold text-regina-gray-800">
                {device.is_online && device.current_state ? formatState(device.current_state) : t('common.na')}
              </span>
            </div>
            <div className="flex justify-between items-center py-2 border-b border-regina-gray-100">
              <span className="text-sm text-regina-gray-600">{t('deviceDetails.lastHeartbeat')}</span>
              <span className="text-sm font-semibold text-regina-gray-800">{formatTimestamp(device.last_heartbeat)}</span>
            </div>
            <div className="flex justify-between items-center py-2">
              <span className="text-sm text-regina-gray-600">{t('deviceDetails.lastSeen')}</span>
              <span className="text-sm font-semibold text-regina-gray-800">{formatTimestamp(device.last_seen)}</span>
            </div>
          </div>
        </div>

        {/* Location Information */}
        <div className="bg-white rounded-xl p-6 border border-regina-gray-100 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-regina-gray-800 flex items-center">
              <MapPinIcon className="w-5 h-5 mr-2 text-regina-red" />
              {t('deviceDetails.locationTitle')}
            </h3>
            {!isEditingLocation ? (
              <button
                onClick={() => setIsEditingLocation(true)}
                className="px-3 py-1 text-sm bg-regina-red text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                {t('deviceDetails.edit')}
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleSaveLocation}
                  disabled={isSaving}
                  className="px-3 py-1 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  {isSaving ? t('deviceDetails.saving') : t('deviceDetails.save')}
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  className="px-3 py-1 text-sm bg-regina-gray-300 text-regina-gray-700 rounded-lg hover:bg-regina-gray-400 transition-colors disabled:opacity-50"
                >
                  {t('deviceDetails.cancel')}
                </button>
              </div>
            )}
          </div>
          {!isEditingLocation ? (
            device.location_name || device.location_address ? (
              <div className="space-y-3">
                {device.location_name && (
                  <div className="flex justify-between items-start gap-3 py-2 border-b border-regina-gray-100">
                    <span className="text-sm text-regina-gray-600 flex-shrink-0">{t('deviceDetails.name')}</span>
                    <span className="text-sm font-semibold text-regina-gray-800 text-right">{device.location_name}</span>
                  </div>
                )}
                {device.location_address && (
                  <div className="flex justify-between items-start gap-3 py-2">
                    <span className="text-sm text-regina-gray-600 flex-shrink-0">{t('deviceDetails.address')}</span>
                    <span className="text-sm font-semibold text-regina-gray-800 text-right break-words">{device.location_address}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-regina-gray-500">
                <MapPinIcon className="w-12 h-12 mx-auto mb-3 text-regina-gray-300" />
                <p className="text-sm font-medium mb-1">{t('deviceDetails.locationUnsetTitle')}</p>
                <p className="text-xs">{t('deviceDetails.locationUnsetHint')}</p>
              </div>
            )
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-regina-gray-700 mb-2">
                  {t('deviceDetails.locationNameLabel')}
                </label>
                <input
                  type="text"
                  value={editedLocationName}
                  onChange={(e) => setEditedLocationName(e.target.value)}
                  className="w-full px-3 py-2 border border-regina-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-regina-red"
                  placeholder={t('deviceDetails.locationNamePlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-regina-gray-700 mb-2">
                  {t('deviceDetails.locationAddressLabel')}
                </label>
                <textarea
                  value={editedLocationAddress}
                  onChange={(e) => setEditedLocationAddress(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-regina-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-regina-red resize-none"
                  placeholder={t('deviceDetails.locationAddressPlaceholder')}
                />
              </div>
            </div>
          )}
        </div>

        {/* Current Metrics */}
        {device.metrics && (
          <div className="bg-white rounded-xl p-6 border border-regina-gray-100 shadow-sm">
            <h3 className="text-lg font-semibold text-regina-gray-800 mb-4 flex items-center">
              <ChartBarIcon className="w-5 h-5 mr-2 text-regina-red" />
              {t('deviceDetails.metricsTitle')}
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-regina-gray-100">
                <span className="text-sm text-regina-gray-600">{t('deviceDetails.cpuUsage')}</span>
                <span className="text-sm font-semibold text-regina-gray-800">{device.metrics.cpu_usage?.toFixed(1) || t('common.na')}%</span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm text-regina-gray-600">{t('deviceDetails.memoryUsage')}</span>
                <span className="text-sm font-semibold text-regina-gray-800">{device.metrics.memory_usage?.toFixed(1) || t('common.na')}%</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Performance Charts */}
      <div className="bg-white rounded-xl p-6 border border-regina-gray-100 shadow-sm">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-regina-gray-800">{t('deviceDetails.performanceTitle')}</h3>
          <div className="flex space-x-2">
            {(['1h', '24h', '7d'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                  timeRange === range
                    ? 'bg-regina-red text-white'
                    : 'bg-regina-gray-100 text-regina-gray-700 hover:bg-regina-gray-200'
                }`}
              >
                {range === '1h' ? t('deviceDetails.range1h') : range === '24h' ? t('deviceDetails.range24h') : t('deviceDetails.range7d')}
              </button>
            ))}
          </div>
        </div>

        {metrics.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* CPU Usage Chart */}
            <div>
              <h4 className="text-regina-gray-800 font-semibold mb-3 flex items-center">
                <CpuChipIcon className="w-5 h-5 mr-2 text-regina-red" />
                {t('deviceDetails.cpuChartTitle')}
              </h4>
              <div className="h-40 bg-regina-gray-50 rounded-lg p-4 flex items-end space-x-1 border border-regina-gray-100">
                {metrics.slice(-20).map((metric, index) => (
                  <div
                    key={index}
                    className="bg-blue-500 rounded-t flex-1 hover:bg-blue-600 transition-colors"
                    style={{ height: `${(metric.cpu_usage || 0) * 0.8}%` }}
                    title={`${metric.cpu_usage?.toFixed(1) || 0}%`}
                  />
                ))}
              </div>
              <div className="mt-2 text-center text-xs text-regina-gray-600">
                {t('deviceDetails.last20Samples')}
              </div>
            </div>

            {/* Memory Usage Chart */}
            <div>
              <h4 className="text-regina-gray-800 font-semibold mb-3 flex items-center">
                <CpuChipIcon className="w-5 h-5 mr-2 text-regina-red" />
                {t('deviceDetails.memoryChartTitle')}
              </h4>
              <div className="h-40 bg-regina-gray-50 rounded-lg p-4 flex items-end space-x-1 border border-regina-gray-100">
                {metrics.slice(-20).map((metric, index) => (
                  <div
                    key={index}
                    className="bg-green-500 rounded-t flex-1 hover:bg-green-600 transition-colors"
                    style={{ height: `${(metric.memory_usage || 0) * 0.8}%` }}
                    title={`${metric.memory_usage?.toFixed(1) || 0}%`}
                  />
                ))}
              </div>
              <div className="mt-2 text-center text-xs text-regina-gray-600">
                {t('deviceDetails.last20Samples')}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center text-regina-gray-600 py-12 bg-regina-gray-50 rounded-lg border border-regina-gray-100">
            {t('deviceDetails.noMetricsData')}
          </div>
        )}
      </div>
    </div>
  );
}

export default DeviceDetails;

